<script setup lang="ts">
import PageLabel from "components/common/PageLabel.vue";
import {useRoute} from "vue-router";
import {TestService} from "@/api/api.ts";
import {reactive, ref} from "vue";
import {AddQuestionForm, EditQuestionForm, TestQuestionInfo, TestSubQuestion as SubQuestion} from "@/types";
import TestQuestion from "components/test/TestQuestion.vue";
import TestSubQuestion from "components/test/TestSubQuestion.vue";
import AddSubQuestionDialog from "components/dialog/test/AddSubQuestionDialog.vue";
import {ElNotification} from "element-plus";
import EditTestQuestionDialog from "components/dialog/test/EditTestQuestionDialog.vue";

const addSubQuestionDialog = ref<InstanceType<typeof AddSubQuestionDialog>>()
const editTestQuestionDialog = ref<InstanceType<typeof EditTestQuestionDialog>>()

const pagination = reactive({
  current: 1,
  pageSize: 10
})
const route = useRoute();
const userId = ref("")
const id = route.params.id as string
const testQuestionInfo = ref<TestQuestionInfo>()

const getTestDetail = async () => {
  try {
    const res = await TestService.getTestQuestionInfo({
      id: id,
      user_id: userId.value,
      page: pagination.current,
      pageSize: pagination.pageSize
    })
    if (res.data.code === 200) {
      testQuestionInfo.value = res.data.data
    }
  } catch (err) {
    console.warn(err)
  }
}

const onAddSubQuestion = () => {
  addSubQuestionDialog.value?.show()
}

const onEditQuestion = () => {
  if (!testQuestionInfo.value) {
    return
  }
  let answer = ""
  if (testQuestionInfo.value.answer && testQuestionInfo.value.answer.length > 0) {
    answer = testQuestionInfo.value.answer.join(",")
  }
  const form: EditQuestionForm = {
    id: testQuestionInfo.value.id,
    type: testQuestionInfo.value.type,
    problem: testQuestionInfo.value.problem,
    answer: answer,
    right_answer: testQuestionInfo.value.right_answer,
    parsing: testQuestionInfo.value.parsing,
    image: testQuestionInfo.value.image,
    right_answer_image: testQuestionInfo.value.right_answer_image,
    score: testQuestionInfo.value.score.toString(),
    score_json: testQuestionInfo.value.score_json,
  }
  editTestQuestionDialog.value?.show(form)
}

const onEditSubQuestion = (item: SubQuestion) => {
  if (!item) {
    return
  }
  let answer = ""
  if (typeof item.answer === "object") {
    if (item.answer && item.answer.length > 0) {
      answer = item.answer.join(",")
    }
  }
  const form: EditQuestionForm = {
    id: item.id,
    type: item.type,
    problem: item.problem,
    answer: answer,
    right_answer: item.right_answer,
    parsing: item.parsing,
    image: item.image,
    right_answer_image: item.right_answer_image,
    score: item.score.toString(),
    score_json: item.score_json,
  }
  editTestQuestionDialog.value?.show(form)
}

const onSubmitAddSubQuestion = async (form: AddQuestionForm) => {
  form.f_cate_id = testQuestionInfo.value?.f_cate_id
  form.s_cate_id = testQuestionInfo.value?.s_cate_id
  form.sort = 2
  form.pid = testQuestionInfo.value?.id
  try {
    // @ts-ignore
    const res = await TestService.addEditTestQuestion(form)
    if (res.data.code === 200) {
      ElNotification.success({
        message: res.data.msg
      })
      addSubQuestionDialog.value?.hide()
      await getTestDetail()
    }
  } catch (err) {
    console.warn(err)
  }
}

const onSubmitEditQuestion = async (form: EditQuestionForm) => {
  try {
    // @ts-ignore
    const res = await TestService.addEditTestQuestion(form)
    if (res.data.code === 200) {
      ElNotification.success({
        message: res.data.msg
      })
      editTestQuestionDialog.value?.hide()
      await getTestDetail()
    }
  } catch (err) {
    console.warn(err)
  }
}

getTestDetail()

</script>

<template>
  <div class="mx-3rem mt-2.25rem">
    <page-label>题目详情</page-label>
    <div class="mt-2rem p-1rem bg-white rounded shadow">
      <test-question
          :test-question-info="testQuestionInfo"
          @on-edit-question="onEditQuestion"
          @on-add-sub-question="onAddSubQuestion"/>
    </div>
    <div class="mt-1rem" v-if="testQuestionInfo?.s_list && testQuestionInfo.s_list.length > 0">
      <div class="mt-1rem text-1.25em">子题</div>
      <template v-for="(item, index) in testQuestionInfo.s_list">
        <div class="mt-1rem p-1rem bg-white rounded shadow" :id="`sub-question-${index + 1}`">
          <test-sub-question
              :title="(index + 1) + '/' + testQuestionInfo.s_list.length" :item="item"
              @edit-sub-question="onEditSubQuestion(item)"/>
        </div>
      </template>
    </div>
  </div>
  <div class="fixed right-1rem top-20rem bg-white shadow"
       v-if="testQuestionInfo?.s_list && testQuestionInfo.s_list.length > 0">
    <div class="mx-.5rem my-.5rem">子题</div>
    <el-anchor :offset="70" style="width:50px;">
      <template v-if="testQuestionInfo?.s_list && testQuestionInfo.s_list.length > 0"
                v-for="(_, index) in testQuestionInfo.s_list">
        <el-anchor-link :href="`#sub-question-${index + 1}`">
          {{ (index + 1) + '/' + testQuestionInfo.s_list.length }}
        </el-anchor-link>
      </template>
    </el-anchor>
  </div>
  <add-sub-question-dialog ref="addSubQuestionDialog" @on-add-question="onSubmitAddSubQuestion"/>
  <edit-test-question-dialog ref="editTestQuestionDialog" @on-edit-question="onSubmitEditQuestion"/>
</template>

<style scoped>
</style>
